import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Topbar } from '@/components/Topbar';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { fmtInt, fmtRelative } from '@/lib/format';
import { haptic } from '@/lib/telegram';
import type { TargetPosting } from '@/types/domain';

/**
 * Деталка одного чата кампании.
 * Сюда юзер попадает кликом на чат из CampaignChatsPage.
 *
 * Показывает:
 *  - шапку чата (health, members, link)
 *  - стек предстоящих постингов (status=scheduled)
 *  - историю прошедших постингов (status=posted/failed)
 *
 * Клик по сообщению/диалогу → confirm "Перейти в чат?" с кнопками
 * "Нет" / "Перейти" (открывает TG ссылку чата в Telegram).
 */
export function CampaignTargetPage(): JSX.Element {
  const { id, targetId } = useParams();
  const cid = Number(id);
  const tid = Number(targetId);
  const { data, loading } = useAsync(() => api.getTargetDetail(cid, tid), [cid, tid]);
  const [confirm, setConfirm] = useState<{ url: string; preview: string } | null>(null);

  const t = data?.target;
  const postings = data?.postings ?? [];

  const { upcoming, history } = useMemo(() => {
    const up: TargetPosting[] = [];
    const hi: TargetPosting[] = [];
    for (const p of postings) {
      if (p.status === 'scheduled' || p.status === 'running') up.push(p);
      else hi.push(p);
    }
    return { upcoming: up, history: hi };
  }, [postings]);

  const chatUrl = useMemo(() => {
    if (!t) return '';
    const link = t.normalizedLink;
    if (!link) return '';
    if (link.startsWith('@')) return `https://t.me/${link.slice(1)}`;
    if (link.startsWith('+')) return `https://t.me/${link}`;
    if (/^https?:\/\//.test(link)) return link;
    return `https://t.me/${link}`;
  }, [t]);

  const openTg = () => {
    if (!chatUrl) return;
    haptic('medium');
    // Через TMA лучше openTelegramLink — но он работает только для t.me ссылок.
    // Если ВНЕ Telegram — просто открываем в новой вкладке.
    const tg: any = (window as any).Telegram?.WebApp;
    if (tg?.openTelegramLink && chatUrl.startsWith('https://t.me/')) {
      tg.openTelegramLink(chatUrl);
    } else {
      window.open(chatUrl, '_blank', 'noopener,noreferrer');
    }
    setConfirm(null);
  };

  if (loading || !t) {
    return (
      <div className="space-y-3">
        <Topbar title="Чат" />
        <div className="skeleton h-24" />
        <div className="skeleton h-32" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-10">
      <Topbar
        title={t.title || t.normalizedLink}
        subtitle={`${t.normalizedLink}${t.membersCount ? ` · ${fmtInt(t.membersCount)} уч.` : ''}`}
        right={<StatusPill status={t.health} withDot={false} />}
      />

      {/* Открыть чат напрямую */}
      {chatUrl && (
        <button
          onClick={() => setConfirm({ url: chatUrl, preview: t.title || t.normalizedLink })}
          className="btn-ghost w-full"
        >
          <Icon name="message" size={16} /> Открыть чат в Telegram
        </button>
      )}

      <Section title="Предстоящие диалоги" count={upcoming.length}>
        {upcoming.length === 0 ? (
          <EmptyState
            icon="sparkles"
            title="Стек пуст"
            body="Нажмите «Распределить акки и диалоги» на странице кампании — мы соберём очередь."
          />
        ) : (
          <ul className="space-y-2">
            {upcoming.map((p) => (
              <PostingCard
                key={p.id}
                p={p}
                onMessageClick={() =>
                  chatUrl &&
                  setConfirm({ url: chatUrl, preview: t.title || t.normalizedLink })
                }
              />
            ))}
          </ul>
        )}
      </Section>

      <Section title="История посевов" count={history.length}>
        {history.length === 0 ? (
          <div className="text-2xs text-ink-500">пока ничего не запостили</div>
        ) : (
          <ul className="space-y-2">
            {history.map((p) => (
              <PostingCard
                key={p.id}
                p={p}
                onMessageClick={() =>
                  chatUrl &&
                  setConfirm({ url: chatUrl, preview: t.title || t.normalizedLink })
                }
              />
            ))}
          </ul>
        )}
      </Section>

      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
          onClick={() => setConfirm(null)}
        >
          <div
            className="w-full max-w-md rounded-t-xl3 border border-ink-700/60 bg-ink-900 p-5 sm:rounded-xl3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-ink-700 sm:hidden" />
            <div className="mb-1 text-base font-semibold text-ink-50">Перейти в чат?</div>
            <div className="mb-4 truncate text-xs text-ink-500">{confirm.preview}</div>
            <div className="flex gap-2">
              <button className="btn-ghost flex-1" onClick={() => setConfirm(null)}>
                Нет
              </button>
              <button className="btn-primary flex-1" onClick={openTg}>
                <Icon name="message" size={14} /> Перейти
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between px-1">
        <h3 className="text-sm font-semibold text-ink-50">{title}</h3>
        <span className="text-2xs text-ink-500">{count}</span>
      </div>
      {children}
    </section>
  );
}

function PostingCard({
  p,
  onMessageClick,
}: {
  p: TargetPosting;
  onMessageClick: () => void;
}): JSX.Element {
  return (
    <li className="card p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="pill pill-primary">{statusLabel(p.status)}</span>
        <span className="text-2xs text-ink-500">{fmtRelative(p.scheduledAt)}</span>
      </div>
      <div className="mt-2 space-y-1.5">
        {p.template.steps.map((s) => (
          <button
            key={s.id}
            onClick={onMessageClick}
            className="block w-full rounded-xl bg-white/4 px-3 py-2 text-left text-xs text-ink-100 transition active:scale-[0.99] hover:bg-white/8"
          >
            <span className="mr-2 text-2xs font-semibold text-accent-300">{s.role}</span>
            <span>{s.text}</span>
            {s.isPitch && (
              <span className="ml-2 align-middle pill pill-primary text-[10px]">pitch</span>
            )}
          </button>
        ))}
      </div>
      {p.lastError && (
        <div className="mt-2 text-2xs text-rose-300/90">err: {p.lastError}</div>
      )}
    </li>
  );
}

function statusLabel(s: TargetPosting['status']): string {
  switch (s) {
    case 'scheduled':
      return 'в очереди';
    case 'running':
      return 'идёт';
    case 'posted':
      return 'отправлен';
    case 'failed':
      return 'ошибка';
    case 'cancelled':
      return 'отменён';
  }
}
